import {
  ApplicationConfig,
  importProvidersFrom,
  provideBrowserGlobalErrorListeners,
  provideAppInitializer,
  provideZoneChangeDetection,
  inject,
  isDevMode,
} from '@angular/core';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withViewTransitions,
} from '@angular/router';

import { routes } from './app.routes';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi} from '@angular/common/http';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { authConfig } from '@modules/auth/auth.config';
import { authInterceptor, OidcSecurityService, provideAuth } from 'angular-auth-oidc-client';
import { APOLLO_NAMED_OPTIONS, provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { InMemoryCache } from '@apollo/client';
import { ApolloLink } from '@apollo/client/link';
import { firstValueFrom } from 'rxjs';
import { environment } from 'environments/environment';
import { provideState, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { effects } from './app-effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { reducers } from './app-reducers';
import { SetContextLink } from '@apollo/client/link/context';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { notificationTemplateFeature } from './store/notification/notification-template.reducer';
import { DatePipe } from '@angular/common';
import { GraphqlErrorInterceptor } from './core/interceptors/graphql-error-interceptor';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NgxPermissionsService } from 'ngx-permissions';
import {AppInterceptor} from '@shared/helpers/app.interceptor';
import {provideAnimations} from '@angular/platform-browser/animations';
import {provideSweetAlert2} from '@sweetalert2/ngx-sweetalert2';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { LocalStorageService } from 'app/storage/local-storage-service';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

// @ts-ignore
export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideBrowserGlobalErrorListeners(),
    provideNativeDateAdapter(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    importProvidersFrom(NgxPermissionsModule.forRoot()),
    provideAppInitializer(() => {
      const permissionsService = inject(NgxPermissionsService);
      const localStorage = inject(LocalStorageService);

      // Ensure permissions are available before guards run on initial navigation.
      permissionsService.flushPermissions();
      permissionsService.loadPermissions(localStorage.getPermissions() || []);
    }),
    provideRouter(routes, withEnabledBlockingInitialNavigation(), withViewTransitions()),
    provideHttpClient(
      withInterceptors([authInterceptor()]),
      withInterceptorsFromDi()
    ),
    provideAuth(authConfig),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    provideSweetAlert2({
      // Optional configuration
      fireOnInit: false,
      dismissOnDestroy: true,
    }),
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        position: { top: '24px' },
        maxWidth: '96vw',
        maxHeight: 'calc(100vh - 48px)',
        autoFocus: false,
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GraphqlErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppInterceptor,
      multi: true,
    },
    // provideHttpClient(),
    DatePipe,
    provideApollo(() => {
      const httpLink = inject(HttpLink);
      const oidcSecurityService = inject(OidcSecurityService);

      const authLink = new SetContextLink(async (prevContext) => {
        const token = await firstValueFrom(oidcSecurityService.getAccessToken());

        return {
          headers: {
            ...prevContext.headers,
            Authorization: `Bearer ${token}`,
          },
        };
      });

      return {
        link: ApolloLink.from([
          authLink,
          httpLink.create({
            uri: environment.GRAPHQL_API_URL_GENERAL,
            withCredentials: true,
          }),
        ]),
        cache: new InMemoryCache(),
      };
    }),
    {
      provide: APOLLO_NAMED_OPTIONS,
      useFactory: (httpLink: HttpLink, oidc: OidcSecurityService) => {
        const authLink = new SetContextLink(async (prevContext) => {
          const token = await firstValueFrom(oidc.getAccessToken());

          return {
            headers: {
              ...prevContext.headers,
              Authorization: `Bearer ${token}`,
            },
          };
        });

        const baseUrl = environment.GRAPHQL_API_URL_GENERAL;

        return {
          [ApolloNamespace.uaa]: {
            link: authLink.concat(
              httpLink.create({
                uri: `${baseUrl}/${ApolloNamespace.uaa}` + `/graphql`,
                withCredentials: true,
              })
            ),
            cache: new InMemoryCache(),
          },
          [ApolloNamespace.settings]: {
            link: authLink.concat(
              httpLink.create({
                uri: `${baseUrl}/${ApolloNamespace.settings}` + `/graphql`,
                withCredentials: true,
              })
            ),
            cache: new InMemoryCache(),
          },
          [ApolloNamespace.notifications]: {
            link: authLink.concat(
              httpLink.create({
                uri: `${baseUrl}/${ApolloNamespace.notifications}` + `/graphql`,
                withCredentials: true,
              })
            ),
            cache: new InMemoryCache(),
          },
          [ApolloNamespace.workflow]: {
            link: authLink.concat(
              httpLink.create({
                uri: `${baseUrl}/${ApolloNamespace.workflow}` + `/graphql`,
                withCredentials: true,
              })
            ),
            cache: new InMemoryCache(),
          },
          [ApolloNamespace.registration]: {
            link: authLink.concat(
              httpLink.create({
                uri: `${baseUrl}/${ApolloNamespace.registration}` + `/graphql`,
                withCredentials: true,
              })
            ),
            cache: new InMemoryCache(),
          },
          [ApolloNamespace.billing]: {
            link: authLink.concat(
              httpLink.create({
                uri: `${baseUrl}/${ApolloNamespace.billing}` + `/graphql`,
                withCredentials: true,
              })
            ),
            cache: new InMemoryCache(),
          },
        };
      },
      deps: [HttpLink, OidcSecurityService],
    },
    provideStore(reducers),
    provideState(notificationTemplateFeature),
    provideEffects(effects),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }), provideCharts(withDefaultRegisterables()),
  ],
};
